/**
 * Copyright (c) 2019, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';
import Linkify from 'react-linkify';
import Localized from '../../../shared/components/Localized';
import Modal from '../../components/Modal';
import styles from '../../styles';
import LoadingSpinner from '../../components/Spinners/LoadingSpinner';
import { ActionIcon } from '../../components/SVGIcon';
import FooterTimeStamp from '../../components/FooterTimestamp';
import Error from '../../components/Error';
import { closeModal } from '../../../../lib/closeModal';

const SectionTitle = styled.div`
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-size: 0.75rem;
  line-height: 1.25;
  color: #54698d;
  text-transform: uppercase;
  letter-spacing: 0.0625rem;
  padding: 0.75rem 1rem;
  background: #f4f6f9;
  border-top: 1px solid #d8dde6;
  border-bottom: 1px solid #d8dde6;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const SubText = styled.span`
  font-size: 0.6875rem;
  font-weight: 400;
  color: rgba(22, 50, 92, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.0625rem;
  padding-right: 8px;
  border-right: 1px solid #d8dde5;
`;

const Wrapper = styled.div`
  border-top-right-radius: 0.25rem;
  border-top-left-radius: 0.25rem;
  padding: 1rem;
  text-align: center;
  background: #fff;
`;

const Title = styled.div`
  ${styles.fonts.headingMedium}
  margin-bottom: 0.5rem;
`;

const GeneralMessageStatus = styled.span`
  padding-left: 8px;
  ${p => (p.isOngoing ? 'color: #04844B' : 'color: #54698D')}
`;

const GeneralMessageHeader = styled.div`
  color: #3f5f80;
  font-size: 1rem;
  letter-spacing: 0px;
  line-height: 1.5rem;
`;

const GeneralMessageIcon = styled(ActionIcon)`
  margin-right: 0.5rem;
`;

const GeneralMessageWrapper = styled.div`
  padding: 1rem;
`;

const GeneralMessageBody = styled.div`
  color: #16325c;
  font-size: 0.875rem;
  letter-spacing: 0px;
  line-height: 1.3125rem;
  padding-top: 1rem;
  white-space: pre-wrap;
`;

const GeneralMessageFooter = styled(FooterTimeStamp)`
  color: #54698d;
  font-size: 0.75rem;
  line-height: 1.25rem;
`;

const isActive = message => {
  return message.endDate === null || moment(message.endDate).isAfter(moment());
};

export const goBack = (history, e) => {
  e.stopPropagation();
  closeModal(history);
};

/**
 * General Message Modal component
 * This component displays a general message by its ID in a modal window
 */
const GeneralMessageModal = ({ requests, history, region }) => {
  const { generalMessage } = requests;
  const { isCompleted, isErrored, value } = generalMessage;
  const message = value;
  let lastModifiedTime = '';

  // We want to show the last updated timestamp if it is different than created time.
  // Currently, If the GM was not updated,
  // API returns non-empty last updated time (same as created time)
  if(message  && !message.createdAt.isSame(message.updatedAt)) {
    lastModifiedTime = message.updatedAt;
  }

  // If there was an error show the error page
  if (isErrored) return <Error />;

  // Show a loading indicator while the request is being processed
  if (!isCompleted) return <LoadingSpinner inverse />;

  const isOngoing = isActive(message);

  let body =  message.body;
  let count = message.InformationalMessageUpdates.length;
  message.InformationalMessageUpdates.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                      .forEach((item, index) => {
                        body = body.concat(`\r\n\r\n*****
                        \r\nUpdate ${count - index}: Posted ${new Date(item.createdAt).toUTCString().replace('GMT', 'UTC')}
                        \r\n${item.message}`)
                      });

  return (
    <Modal
      onClose={event => {
        goBack(history, event);
      }}
    >
      <Wrapper>
        <Title>
          <Localized
            modelName={'statusKey'}
            modelKey={'informational'}
            modelAttribute={'label'}
          />
        </Title>
      </Wrapper>
      <SectionTitle>
        <div>
          <Localized
            modelName={'statusKey'}
            modelKey={'informational'}
            modelAttribute={'label'}
          />{' '}
          <Localized
            modelName={'generalMessage'}
            modelKey={'details'}
            modelAttribute={'label'}
          />
        </div>
        <div>
          <SubText>{`ID# ${message.id}`}</SubText>
          <GeneralMessageStatus isOngoing={isOngoing}>
            {isOngoing ? (
              <Localized
                modelName={'modal'}
                modelKey={'status'}
                modelAttribute={'valueOngoing'}
              />
            ) : (
              <Localized
                modelName={'generalMessage'}
                modelKey={'closed'}
                modelAttribute={'label'}
              />
            )}
          </GeneralMessageStatus>
        </div>
      </SectionTitle>
      <GeneralMessageWrapper>
        <GeneralMessageHeader>
          <GeneralMessageIcon bgColor="#00a1e0" icon="announcement" circle />
          {message.subject}
        </GeneralMessageHeader>
        <GeneralMessageBody>
          {' '}
          <Linkify properties={{ target: '_blank' }}>{body}</Linkify>
        </GeneralMessageBody>
        <GeneralMessageFooter
          createdTime={message.createdAt}
          lastModifiedTime={lastModifiedTime}
          region={region}
        />
      </GeneralMessageWrapper>
    </Modal>
  );
};

GeneralMessageModal.propTypes = {
  requests: PropTypes.object,
  history: PropTypes.object,
  region: PropTypes.string
};

export default GeneralMessageModal;
